import LivenessCheck from "@/components/Verification/liveness_check/LivenessCheck.vue";
import ClientLogo from "@/components/ClientLogo/ClientLogo.vue";
import { MAIN_CONTROLLER } from "@/utils/main_controller.js";
import { API_BASE_URL } from "@/config";
import { validate_id } from '@/utils/validations';
import { PREPOPULATE_INPUT } from "@/utils/prepopulate_input";

export default {
  name: "FacialBiometricsComponent",
  emits: ["set-liveness-data"],
  mixins: [MAIN_CONTROLLER, PREPOPULATE_INPUT],
  data: () => ({
    photo_src: "",
    get_consent: false,
    consent_provided: false,
    is_id_valid: false,
    showCamera: false,
    showInstructions: false,
    show_error: false,
    error_message: "",
    id_number: "",
    dataLoaded: false,
    hasPrepopulatedNames: false,
  }),
  computed: {
    workflow_id() {
      return this.$store.getters.get_workflow_id;
    },

    showNameInputs() {
      return this.dataLoaded && !this.hasPrepopulatedNames;
    },

    formHeaderText() {
      if (this.showNameInputs) {
        return "Please Provide Your Details";
      }
      return "Please Provide your ID Number";
    },

    namesAreValid() {
      return this.first_name && this.last_name && 
             this.first_name.trim().length > 0 && 
             this.last_name.trim().length > 0;
    },
    isFormValid() {
      const idValid = this.is_id_valid;
      return idValid && this.namesAreValid;
    }
  },
  created() {
    this.consent_provided =
      localStorage.getItem("facial_biometrics_consent") === "true";
  },
  components: {
    LivenessCheck,
    ClientLogo,
  },
  methods: {
    async get_customer_data() {
      const TOKEN = localStorage.getItem('token');
      const CUSTOMER_DATA_URL = `${API_BASE_URL}/verify_api/system_management_api/get_customer_data/`;

      try {
        await $.ajax({
          url: CUSTOMER_DATA_URL,
          method: 'GET',
          contentType: 'application/json',
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          beforeSend: () => {
            Swal.fire({
              text: 'Loading ...',
              allowEscapeKey: false,
              allowOutsideClick: false,
              didOpen: () => Swal.showLoading(),
            });
          },
          success: (response) => {
            Swal.close();
            this.form_data = { ...this.form_data, ...response };
            this.sync_form_data();
            
            const hasFirstName = response.first_name && response.first_name.trim().length > 0;
            const hasLastName = response.last_name && response.last_name.trim().length > 0;
            this.hasPrepopulatedNames = hasFirstName && hasLastName;
          },
        });
      } catch (error) {
        Swal.close();
        console.error("Error fetching customer data:", error);
        this.hasPrepopulatedNames = false;
      }
    },
    
    async id_check() {
      this.error_message = "";

      if (!this.namesAreValid) {
        this.error_message = "Please enter both first and last name";
        return;
      }

      if (this.validate_id_number()) {
        const TOKEN = localStorage.getItem("token");
        const CONSENT_URL = `${API_BASE_URL}/verify_api/system_management_api/save_id_verification_api/`;
        const FORM_DATA = {
          id_number: this.id_number,
          first_name: (this.first_name || '').trim(),
          last_name: (this.last_name || '').trim(),
          workflow_id: this.workflow_id,
        };

        $.ajax({
          url: CONSENT_URL,
          method: "POST",
          contentType: "application/json",
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          data: JSON.stringify(FORM_DATA),
          beforeSend: () => {
            this.error_message = "";
          },
          success: (response) => {
            this.error_message = "";
            if (response.status === "success") {
              localStorage.setItem("id_number", response.id_number);
              localStorage.setItem("user_state", "id_verified");
              this.showInstructions = true;
            }
          },
          error: (error) => {
            if (error.responseJSON) {
              this.error_message = error.responseJSON.id_number?.[0] || 
                                 error.responseJSON.first_name?.[0] || 
                                 error.responseJSON.last_name?.[0] || 
                                 "ID number already exists.";
            } else {
              this.error_message = "Something went wrong!";
            }
          },
        });
      } else {
        this.error_message = "Invalid ID number.";
      }
    },
    validate_id_number() {
      this.is_id_valid = validate_id(this.id_number);
      return this.is_id_valid;
    },
    clear_error_message() {
      this.error_message = "";
      this.show_error = false;
    },
    set_photo_src(photo_src) {
      this.photo_src = photo_src;
      this.$refs.liveness_check.close_modal();
      this.$emit("set-liveness-data", {
        photo: photo_src,
        id_number: this.id_number,
        firstnames: (this.first_name || '').trim(),
        lastname: (this.last_name || '').trim(),
      });
    },
    async startFaceRecognition() {
      this.showCamera = false;
      this.showInstructions = false;

      this.$refs.liveness_check.unlockAudio();

      await new Promise(resolve => setTimeout(resolve, 100));

      this.showCamera = true;

      try {
        await this.$refs.liveness_check.startFaceRecognition();
      } catch (error) {
        console.error("Error starting face recognition:", error);
        Swal.fire({
          icon: "error",
          text: "Unable to start facial recognition. Please try again.",
        });
        this.showCamera = false;
        this.showInstructions = true;
      }
    },
    start_liveness_check() {
      this.$refs.liveness_check.set_id_number(this.client_id);
    },
    get_terms_and_conditions() {
      const TOKEN = localStorage.getItem("token");
      const GET_TERMS_AND_CONDITIONS_API = `${API_BASE_URL}/verify_api/system_management_api/get_terms_and_conditions_api/`;
      $.ajax({
        url: GET_TERMS_AND_CONDITIONS_API,
        method: "GET",
        contentType: "application/json",
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        beforeSend: () => {
          Swal.fire({
            text: "Loading ...",
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });
        },
        success: (response) => {
          Swal.close();

          this.get_consent = response.get_consent;
          this.consent_provided = response.get_consent;

          if (response.get_consent) {
            localStorage.setItem("facial_biometrics_consent", "true");
            localStorage.setItem("user_state", "consent_given");
          }
        },
        error: () => {
          Swal.close();
        },
      });
    },
    accept() {
      const TOKEN = localStorage.getItem("token");
      const CONCENT_URL = `${API_BASE_URL}/verify_api/system_management_api/terms_and_conditions_api/`;

      const FORM_DATA = {
        consent_provided: true,
      };

      $.ajax({
        url: CONCENT_URL,
        method: "POST",
        contentType: "application/json",
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        data: JSON.stringify(FORM_DATA),
        beforeSend: () => {
          Swal.fire({
            text: "Loading ...",
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });
        },
        success: () => {
          Swal.close();
          this.consent_provided = true;
          localStorage.setItem("facial_biometrics_consent", "true");
          localStorage.setItem("user_state", "consent_given");

          const modal = document.getElementById("terms_and_conditions_modal");
          if (modal) {
            modal.style.display = "none";
          }
          this.$emit("consent-updated", true);
        },
        error: () => {
          Swal.close();
          Swal.fire({
            icon: "error",
            text: "Error saving consent. Please try again.",
          });
        },
      });
    },
    decline() {
      Swal.fire({
        title: "Are you sure you want to decline?",
        text: "You cannot proceed with the application if consent is not provided.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.isConfirmed) {
          localStorage.removeItem("token");
          localStorage.removeItem("user_state");
          this.$router.push("/");
        }
      });
    },
    show_popup() {
      const modal = document.getElementById("terms_and_conditions_modal");
      if (modal) {
        modal.style.display = "block";
      }
    },
    hide_popup() {
      const modal = document.getElementById("terms_and_conditions_modal");
      if (modal) {
        modal.style.display = "none";
      }
    },
  },
  async mounted() {
    this.get_terms_and_conditions();
    const user_state = localStorage.getItem("user_state");
    if (user_state === "consent_given") {
      this.consent_provided = true;
    }

    try {
      await this.get_customer_data();
    } catch (error) {
      console.error("Error fetching customer data:", error);
    } finally {
      this.dataLoaded = true;
    }
  },
  watch: {
    get_consent(consent) {
      if (consent) {
        this.hide_popup();
      } else {
        this.show_popup();
      }
    },
    showCamera(newValue) {
      this.$emit("camera-state-changed", newValue);
    },
  },
};
